import React from 'react';
import { UseFormRegister, FieldErrors } from 'react-hook-form';
import { Deduction } from '../../../types';
import Input from '../Input';
import Select from '../Select';

interface DeductionDetailsSectionProps {
  register: UseFormRegister<Deduction>;
  errors: FieldErrors<Deduction>;
  deductionType: string;
  currencyOptions: { value: string; label: string }[];
  invoiceCurrencyCode: string;
  deductionCurrencyCode: string;
  amountValue: number;
  percentageValue: number;
  exchangeRateValue: number;
  invoiceTotal: number;
}

const typeOptions = [
  { value: 'percentage', label: 'Percentage Deduction' },
  { value: 'fixed', label: 'Fixed Amount Deduction' },
  { value: 'performance_bond', label: 'Performance Bond' },
  { value: 'tax', label: 'Tax Deduction' }
];

const statusOptions = [
  { value: 'active', label: 'Active' },
  { value: 'applied', label: 'Applied' },
  { value: 'cancelled', label: 'Cancelled' }
];

const reasonOptions = [
  { value: 'performance_bond', label: 'Performance Bond' },
  { value: 'tax_withholding', label: 'Tax Withholding' },
  { value: 'penalty', label: 'Penalty' },
  { value: 'advance_payment', label: 'Advance Payment Deduction' },
  { value: 'equipment_damage', label: 'Equipment Damage' },
  { value: 'other', label: 'Other' }
];

const DeductionDetailsSection: React.FC<DeductionDetailsSectionProps> = ({
  register,
  errors,
  deductionType,
  currencyOptions,
  invoiceCurrencyCode,
  deductionCurrencyCode,
  amountValue,
  percentageValue,
  exchangeRateValue,
  invoiceTotal,
}) => {
  const isPercentage = deductionType === 'percentage';
  // Fixed-amount deductions in a different currency than the invoice need a
  // rate to EGP, exactly like transactions (EGP amount ÷ rate = foreign amount)
  const currencyMismatch =
    !isPercentage && !!invoiceCurrencyCode && !!deductionCurrencyCode &&
    invoiceCurrencyCode !== deductionCurrencyCode;
  const rateCurrency = deductionCurrencyCode !== 'EGP' ? deductionCurrencyCode : invoiceCurrencyCode;

  const conversionApplies = currencyMismatch && (amountValue || 0) > 0 && (exchangeRateValue || 0) > 0;
  const appliedToInvoice = isPercentage
    ? (invoiceTotal * (percentageValue || 0)) / 100
    : !currencyMismatch ? (amountValue || 0)
    : !conversionApplies ? 0
    : deductionCurrencyCode === 'EGP' ? (amountValue / exchangeRateValue)
    : invoiceCurrencyCode === 'EGP' ? (amountValue * exchangeRateValue)
    : amountValue;
  const showPreview = !!invoiceCurrencyCode && appliedToInvoice > 0;

  return (
    <div className="card">
      <div className="card-header">
        <h2 className="text-lg font-semibold text-gray-900">Deduction Details</h2>
      </div>
      <div className="card-body space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <Select
              label="Deduction Type"
              {...register('type', { required: 'Deduction type is required' })}
              options={typeOptions}
              error={errors.type?.message}
            />
          </div>
          <div>
            <Select
              label="Reason"
              {...register('reason', { required: 'Reason is required' })}
              options={reasonOptions}
              error={errors.reason?.message}
            />
          </div>

          {isPercentage ? (
            <div>
              <Input
                label="Percentage (%)"
                type="number"
                step="0.01"
                min="0"
                max="100"
                {...register('percentage', {
                  required: 'Percentage is required',
                  valueAsNumber: true,
                  min: { value: 0, message: 'Percentage must be 0 or greater' },
                  max: { value: 100, message: 'Percentage cannot exceed 100%' }
                })}
                error={errors.percentage?.message}
              />
            </div>
          ) : (
            <div>
              <Input
                label="Amount"
                type="number"
                step="0.01"
                min="0"
                {...register('amount', {
                  required: 'Amount is required',
                  valueAsNumber: true,
                  min: { value: 0, message: 'Amount must be 0 or greater' }
                })}
                error={errors.amount?.message}
              />
            </div>
          )}

          <div>
            <Select
              label="Currency"
              {...register('currency', { required: 'Currency is required' })}
              options={currencyOptions}
              error={errors.currency?.message}
            />
          </div>

          {currencyMismatch && (
            <div>
              <Input
                label={`Exchange Rate (${rateCurrency} to EGP)`}
                type="number"
                step="0.01"
                required
                {...register('exchangeRate', {
                  valueAsNumber: true,
                  min: { value: 0.01, message: 'Rate must be positive' }
                })}
                error={errors.exchangeRate?.message}
                placeholder="e.g., 50.00"
                helperText={deductionCurrencyCode === 'EGP'
                  ? `EGP amount ÷ rate = ${invoiceCurrencyCode} deducted from the invoice`
                  : undefined}
              />
            </div>
          )}

          <div>
            <Select
              label="Status"
              {...register('status', { required: 'Status is required' })}
              options={statusOptions}
              error={errors.status?.message}
            />
          </div>
        </div>

        {showPreview && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-sm text-blue-800">
              Deducted from the invoice:{' '}
              <strong>
                {appliedToInvoice.toLocaleString(undefined, { maximumFractionDigits: 2 })} {invoiceCurrencyCode}
              </strong>
              {' '}
              {isPercentage
                ? `(${percentageValue || 0}% of ${invoiceTotal.toLocaleString()} ${invoiceCurrencyCode})`
                : currencyMismatch
                  ? (deductionCurrencyCode === 'EGP'
                    ? `(${Number(amountValue).toLocaleString()} EGP ÷ ${exchangeRateValue})`
                    : `(${Number(amountValue).toLocaleString()} ${deductionCurrencyCode} × ${exchangeRateValue})`)
                  : ''}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DeductionDetailsSection;
